import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ReportType } from '@fdm/api-client';

@Component({
  selector: 'app-change-report-type',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ],
  templateUrl: './change-report-type.component.html',
  styleUrls: ['./change-report-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeReportTypeDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ChangeReportTypeDialogComponent>);
  readonly data = inject<ChangeReportTypeDialogPayload>(MAT_DIALOG_DATA);

  subheading = signal<string>('Are you sure you want to change the report type?');
  reportTypeLabel = signal<string>('New Report Type:');
  justificationLabel = signal<string>('Justification:');
  confirmText = signal<string>('Confirm');
  cancelText = signal<string>('Cancel');
  footerNote = signal<string>('Justification is required');

  selectedReportType = signal<ReportType | null>(null);
  justification = signal<string>('');
  errorMessage = signal<string>('');

  reportTypeOptions = computed(() => {
    console.log(this.data.options);

    return this.data.options.map(opt => ({
      value: opt.value,
      text: this.getReportTypeLabel(opt.value)
    }));
  });

  constructor() {
    if (this.data.options && this.data.options.length > 0) {
      this.selectedReportType.set(this.data.options[0].value);
    }
  }

  public getReportTypeLabel(reportType: ReportType): string {
    switch (reportType) {
      case ReportType.IntegrityDeficiency:
        return 'Integrity Deficiency';
      case ReportType.ReliabilityDeficiency:
        return 'Reliability Deficiency';
      case ReportType.AnomalyReport:
        return 'Anomaly Report';
      case ReportType.IntegrityThreat:
        return 'Integrity Threat';
      case ReportType.ReliabilityThreat:
        return 'Reliability Threat';
      default:
        return '';
    }
  }

  checkValidation(): boolean {
    const justificationValue = this.justification().trim();

    if (justificationValue === '') {
      this.errorMessage.set('Justification is required');
      return false;
    }

    this.errorMessage.set('');
    return true;
  }

  confirmAction(): void {
    if (this.checkValidation()) {
      this.dialogRef.close({
        status: 'Success',
        justification: this.justification(),
        reportType: this.selectedReportType()!
      });
    }
  }

  cancelAction(): void {
    this.dialogRef.close();
  }
}

export interface ChangeReportTypeDialogPayload {
  options: Array<{ value: ReportType }>;
  subheading?: string;
  reportTypeLabel?: string;
  justificationLabel?: string;
  confirmText?: string;
  cancelText?: string;
  footerNote?: string;
}

export interface ChangeReportTypeDialogResult {
  status: 'Success';
  justification: string;
  reportType: ReportType;
}
